import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const DateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

const PersonSchema = z.object({
  position: z.number().int().min(1).max(10),
  is_baby: z.boolean().default(false),
  gender: z.string().trim().max(20).optional().nullable(),
  family_name: z.string().trim().max(120).optional().nullable(),
  first_name: z.string().trim().max(120).optional().nullable(),
  nationality: z.string().trim().max(80).optional().nullable(),
  date_of_birth: DateString.optional().nullable().or(z.literal("")),
  document_number: z.string().trim().max(80).optional().nullable(),
  document_issue_date: DateString.optional().nullable().or(z.literal("")),
  document_expiry_date: DateString.optional().nullable().or(z.literal("")),
  document_type: z.enum(["passport", "id", ""]).optional().nullable(),
});

const TravelRowSchema = z.object({
  date: z.string().trim().max(40).optional().default(""),
  airport: z.string().trim().max(120).optional().default(""),
  flight_number: z.string().trim().max(40).optional().default(""),
  departure_time: z.string().trim().max(40).optional().default(""),
  arrival_time: z.string().trim().max(40).optional().default(""),
});

const SubmitSchema = z.object({
  token: z.string().trim().min(20).max(128),
  renter_family_name: z.string().trim().max(120).optional().default(""),
  renter_first_name: z.string().trim().max(120).optional().default(""),
  renter_address: z.string().trim().max(255).optional().default(""),
  renter_postcode: z.string().trim().max(40).optional().default(""),
  renter_city: z.string().trim().max(120).optional().default(""),
  renter_country: z.string().trim().max(120).optional().default(""),
  renter_email: z.string().trim().email().max(255).optional().or(z.literal("")),
  renter_phone: z.string().trim().max(40).optional().default(""),
  renter_iban: z.string().trim().max(64).optional().default(""),
  bedrooms_requested: z.coerce.number().int().min(1).max(4).optional().nullable(),
  baby_kit: z.boolean().default(false),
  travel_mode: z.enum(["car", "plane", ""]).optional().default(""),
  travel_info: z.array(TravelRowSchema).max(10).default([]),
  found_via: z.string().trim().max(255).optional().default(""),
  early_check_in: z.boolean().default(false),
  late_check_out: z.boolean().default(false),
  persons: z.array(PersonSchema).max(10).default([]),
});

export type GuestInfoSubmitInput = z.infer<typeof SubmitSchema>;

function nullify<T extends string | undefined | null>(v: T): string | null {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

export const getGuestInfoByToken = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => z.object({ token: z.string().min(10).max(128) }).parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: sub, error } = await supabaseAdmin
      .from("guest_info_submissions")
      .select(
        "id, booking_id, submitted_at, renter_family_name, renter_first_name, renter_address, renter_postcode, renter_city, renter_country, renter_email, renter_phone, renter_iban, bedrooms_requested, baby_kit, travel_mode, travel_info, found_via, early_check_in, late_check_out",
      )
      .eq("token", data.token)
      .maybeSingle();
    if (error) {
      console.error("[getGuestInfoByToken]", error);
      throw new Error("Could not load form.");
    }
    if (!sub) return { ok: false as const, reason: "not_found" as const };

    const { data: booking } = await supabaseAdmin
      .from("bookings")
      .select("reference, name, email, phone, country, check_in, check_out, bedrooms, adults, children, babies, status")
      .eq("id", sub.booking_id)
      .maybeSingle();

    const { data: persons } = await supabaseAdmin
      .from("guest_info_persons")
      .select("position, is_baby, gender, family_name, first_name, nationality, date_of_birth, document_number, document_issue_date, document_expiry_date, document_type")
      .eq("submission_id", sub.id)
      .order("position", { ascending: true });

    return {
      ok: true as const,
      booking,
      submission: { ...sub, persons: persons ?? [] },
    };
  });

export const submitGuestInfo = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => SubmitSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: sub, error: lookupErr } = await supabaseAdmin
      .from("guest_info_submissions")
      .select("id, booking_id")
      .eq("token", data.token)
      .maybeSingle();
    if (lookupErr) {
      console.error("[submitGuestInfo] lookup", lookupErr);
      throw new Error("Could not save form. Please try again.");
    }
    if (!sub) return { ok: false as const, reason: "not_found" as const };

    const { error: updErr } = await supabaseAdmin
      .from("guest_info_submissions")
      .update({
        renter_family_name: nullify(data.renter_family_name),
        renter_first_name: nullify(data.renter_first_name),
        renter_address: nullify(data.renter_address),
        renter_postcode: nullify(data.renter_postcode),
        renter_city: nullify(data.renter_city),
        renter_country: nullify(data.renter_country),
        renter_email: nullify(data.renter_email ?? ""),
        renter_phone: nullify(data.renter_phone),
        renter_iban: nullify((data.renter_iban ?? "").replace(/\s+/g, "").toUpperCase()),
        bedrooms_requested: data.bedrooms_requested ?? null,
        baby_kit: data.baby_kit,
        travel_mode: nullify(data.travel_mode),
        travel_info: data.travel_info,
        found_via: nullify(data.found_via),
        early_check_in: data.early_check_in,
        late_check_out: data.late_check_out,
        submitted_at: new Date().toISOString(),
      })
      .eq("id", sub.id);
    if (updErr) {
      console.error("[submitGuestInfo] update", updErr);
      throw new Error("Could not save form. Please try again.");
    }

    if (sub.booking_id) {
      try {
        await supabaseAdmin
          .from("bookings")
          .update({
            early_check_in: data.early_check_in,
            late_check_out: data.late_check_out,
          })
          .eq("id", sub.booking_id);
      } catch (e) {
        console.error("[submitGuestInfo] sync booking flags", e);
      }
    }

    await supabaseAdmin.from("guest_info_persons").delete().eq("submission_id", sub.id);
    if (data.persons.length > 0) {
      const rows = data.persons.map((p) => ({
        submission_id: sub.id,
        position: p.position,
        is_baby: p.is_baby,
        gender: nullify(p.gender ?? null),
        family_name: nullify(p.family_name ?? null),
        first_name: nullify(p.first_name ?? null),
        nationality: nullify(p.nationality ?? null),
        date_of_birth: p.date_of_birth && p.date_of_birth.length ? p.date_of_birth : null,
        document_number: nullify(p.document_number ?? null),
        document_issue_date:
          p.document_issue_date && p.document_issue_date.length ? p.document_issue_date : null,
        document_expiry_date:
          p.document_expiry_date && p.document_expiry_date.length ? p.document_expiry_date : null,
        document_type: p.document_type && p.document_type.length ? p.document_type : null,
      }));
      const { error: insErr } = await supabaseAdmin.from("guest_info_persons").insert(rows);
      if (insErr) {
        console.error("[submitGuestInfo] persons", insErr);
        throw new Error("Could not save guests. Please try again.");
      }
    }

    // Confirmation email to the renter (fire-and-forget, multilingual).
    try {
      if (sub.booking_id) {
        const { data: booking } = await supabaseAdmin
          .from("bookings")
          .select(
            "reference, name, email, country, check_in, check_out, adults, children, babies, preferred_language",
          )
          .eq("id", sub.booking_id)
          .maybeSingle();

        const recipient = nullify(data.renter_email ?? "") ?? booking?.email ?? null;
        if (booking && recipient) {
          const { resolveLocale } = await import("./email-templates/i18n");
          const { sendTransactionalEmail } = await import("./email-sender.server");
          const locale = resolveLocale({
            preferred: (booking as any).preferred_language ?? null,
            country: booking.country ?? null,
          });
          const guestName =
            [nullify(data.renter_first_name), nullify(data.renter_family_name)]
              .filter(Boolean)
              .join(" ") || booking.name || "";
          const guestCount =
            (booking.adults ?? 0) + (booking.children ?? 0) + (booking.babies ?? 0);
          await sendTransactionalEmail({
            templateName: "guest-info-received",
            recipientEmail: recipient,
            idempotencyKey: `guest-info-received-${sub.id}`,
            templateData: {
              guestName,
              reference: booking.reference,
              checkIn: booking.check_in,
              checkOut: booking.check_out,
              guestCount,
              locale,
            },
          });
        }
      }
    } catch (e) {
      console.error("[submitGuestInfo] confirmation email failed", e);
    }

    return { ok: true as const };
  });

export const getGuestInfoForBooking = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ bookingId: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: sub } = await supabaseAdmin
      .from("guest_info_submissions")
      .select("*")
      .eq("booking_id", data.bookingId)
      .maybeSingle();
    if (!sub) return { exists: false as const };
    const { data: persons } = await supabaseAdmin
      .from("guest_info_persons")
      .select("*")
      .eq("submission_id", sub.id)
      .order("position", { ascending: true });
    return { exists: true as const, submission: sub, persons: persons ?? [] };
  });

export const createGuestInfoSubmission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ bookingId: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: existing } = await supabaseAdmin
      .from("guest_info_submissions")
      .select("token")
      .eq("booking_id", data.bookingId)
      .maybeSingle();
    if (existing) {
      return { ok: true as const, alreadyExisted: true as const, token: existing.token };
    }

    const { data: booking, error: bErr } = await supabaseAdmin
      .from("bookings")
      .select("name, email, phone, country, bedrooms")
      .eq("id", data.bookingId)
      .maybeSingle();
    if (bErr) throw new Error(bErr.message);
    if (!booking) throw new Error("Booking not found");

    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    const token = Array.from(bytes)
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("");

    const nameParts = String(booking.name ?? "").trim().split(/\s+/);
    const first = nameParts.slice(0, -1).join(" ") || nameParts[0] || null;
    const family = nameParts.length > 1 ? nameParts.slice(-1)[0] : null;

    const { error: insErr } = await supabaseAdmin
      .from("guest_info_submissions")
      .insert({
        booking_id: data.bookingId,
        token,
        renter_family_name: family,
        renter_first_name: first,
        renter_email: booking.email ?? null,
        renter_phone: booking.phone ?? null,
        renter_country: booking.country ?? null,
        bedrooms_requested: booking.bedrooms ?? null,
      });
    if (insErr) throw new Error(insErr.message);

    return { ok: true as const, alreadyExisted: false as const, token };
  });